import { CurrencyPipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, FormField, required } from '@angular/forms/signals';
import { BuscadorCliente } from '@shared/components/buscador-cliente/buscador-cliente';
import { Tabla } from '@shared/components/tabla/tabla';
import { Paginacion } from '@shared/components/paginacion/paginacion';
import { FiltrosReporte } from '@shared/components/filtros-reporte/filtros-reporte';
import { UiDatepicker, UiDialog, UiInputNumber, UiSelect, UiTooltip } from '@shared/components/ui';
import { FacturacionApiService } from '@core/services/api/facturacion-api.service';
import { GuiaRemisionApiService } from '@core/services/api/guia-remision-api.service';
import { ProductoApiService } from '@core/services/api/producto.service';
import { AppService } from '@shared/services/app.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { ToastService } from '@shared/services/toast.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { IProductoData } from '@core/interfaces/entidades/producto.entidad';
import { IClienteData } from '@core/interfaces/entidades/cliente.entidad';
import { ETipoDocumentoSUNAT } from '@core/enums/tipoDocumentoSUNAT.enum';
import { IItemFacturacion } from '@core/interfaces/request-response/facturacion.dto.interface';
import {
  IItemGuia,
  IDocumentoRelacionado,
  ITransportista,
  ICrearGuiaRemisionReq,
} from '@core/interfaces/request-response/guia-remision.dto.interface';
import { environment } from '@env/environment';
import { redondearDosDecimales, calcularAtajoRangoFecha } from '@shared/utils/functions.utils';
import { CrearProducto } from '@shared/components/crear-producto/crear-producto';
import { WhatsAppPhoneInput } from '@shared/components/whatsapp-phone-input/whatsapp-phone-input';
import { CategoriaApiService } from '@core/services/api/categoria-api.service';
import { ICategoriaData } from '@core/interfaces/entidades/categoria.entidad';
import { ITablaColumna } from '@core/interfaces/tabla.interface';
import { ETipoUnidadSUNAT } from '@core/enums/tipoUnidadSUNAT.enum';
import { SunatRespuestaPipe } from '@shared/pipes/sunat-respuesta.pipe';
import { ETipoDocumento } from '@core/enums/tipoDocumento.enum';
import { EAtajoRangoTiempo } from '@core/enums/atajoRangoTiempo.enum';
import { IOptionsSelect } from '@core/interfaces/options.select.interface';
import { IPaginar } from '@core/interfaces/paginacion/paginacion.interface';

const UNIDAD_SUNAT_LABELS: Record<string, string> = {
  NIU: 'Unidad (bienes)',
  ZZ: 'Unidad (servicios)',
  KGM: 'Kilogramo',
  LTR: 'Litro',
  BX: 'Caja',
  BG: 'Bolsa',
  BO: 'Botellas',
  C62: 'Piezas',
  DZN: 'Docena',
  GRM: 'Gramo',
  MTR: 'Metro',
  MTK: 'Metro cuadrado',
  MTQ: 'Metro cúbico',
  TNE: 'Toneladas',
  GLL: 'US Galón',
  PK: 'Paquete',
  RM: 'Resma',
  SET: 'Juego',
};
function buildUnidadesSunat(): { label: string; value: string }[] {
  return Object.values(ETipoUnidadSUNAT).map((v) => ({
    label: `${UNIDAD_SUNAT_LABELS[v] || v} (${v})`,
    value: v,
  }));
}

const MOTIVOS_TRASLADO = [
  { label: '01 - Venta', value: '01' },
  { label: '02 - Compra', value: '02' },
  { label: '04 - Traslado entre establecimientos', value: '04' },
  { label: '05 - Consignación', value: '05' },
  { label: '06 - Devolución', value: '06' },
  { label: '08 - Importación', value: '08' },
  { label: '09 - Exportación', value: '09' },
  { label: '13 - Otros', value: '13' },
];
const MODALIDADES = [
  { label: '01 - Transporte público', value: '01' },
  { label: '02 - Transporte privado', value: '02' },
];

type ETipoHistorial = 'TODOS' | 'FACTURA' | 'BOLETA' | 'GRE';
const OPCIONES_TIPO: IOptionsSelect<ETipoHistorial>[] = [
  { label: 'Todos', value: 'TODOS' },
  { label: 'Factura', value: 'FACTURA' },
  { label: 'Boleta', value: 'BOLETA' },
  { label: 'Guía de Remisión', value: 'GRE' },
];
const OPCIONES_ESTADO: IOptionsSelect<string | null>[] = [
  { label: 'Todos', value: null },
  { label: 'Aceptado', value: 'ACEPTADO' },
  { label: 'Rechazado', value: 'RECHAZADO' },
  { label: 'Pendiente', value: 'PENDIENTE' },
  { label: 'Anulado', value: 'ANULADO' },
  { label: 'Borrador', value: 'BORRADOR' },
];

@Component({
  selector: 'app-facturacion',
  imports: [
    CurrencyPipe,
    FormsModule,
    FormField,
    SunatRespuestaPipe,
    BuscadorCliente,
    Tabla,
    Paginacion,
    FiltrosReporte,
    UiSelect,
    UiDialog,
    UiDatepicker,
    UiInputNumber,
    UiTooltip,
    CrearProducto,
    WhatsAppPhoneInput,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './facturacion.html',
})
export class Facturacion implements OnInit {
  private facturacionApi = inject(FacturacionApiService);
  private guiaApi = inject(GuiaRemisionApiService);
  private productoApi = inject(ProductoApiService);
  private categoriaApi = inject(CategoriaApiService);
  private appService = inject(AppService);
  private sucursalService = inject(SucursalService);
  private toastService = inject(ToastService);
  private localStorageService = inject(LocalStorageService);

  vista = signal<'emitir' | 'guias' | 'historial'>('emitir');
  unidadesSunat = buildUnidadesSunat();
  maxDate = new Date(new Date().setHours(23, 59, 59, 999));

  // ═══════════ COMPROBANTES ═══════════
  modalCrearProducto = signal(false);
  categorias = signal<ICategoriaData[]>([]);
  tipoDocumentos = signal([ETipoDocumentoSUNAT.FACTURA, ETipoDocumentoSUNAT.BOLETA]);
  formFacturacion = form(
    signal({
      tipo_documento: (this.localStorageService.getUltimoTipoDocumentoSUNAT() ===
      ETipoDocumentoSUNAT.FACTURA
        ? ETipoDocumentoSUNAT.FACTURA
        : ETipoDocumentoSUNAT.BOLETA) as ETipoDocumentoSUNAT,
      igv_porcentaje: 0.18,
      descuento: 0,
      observaciones: '',
      cliente: null as IClienteData | null,
      descontar_inventario: false,
    }),
    (p) => {
      required(p.tipo_documento);
    },
  );
  productoManual = signal({
    descripcion: '',
    cantidad: 1,
    precio_unitario: 0,
    unidad: 'NIU' as string,
    descuento: 0,
  });
  items = signal<IItemFacturacion[]>([]);
  cargando = signal(false);
  sendWhatsApp = signal(false);
  whatsAppCountryCode = signal('+51');
  whatsAppNumber = signal('');
  disableWhatsApp = computed(() => {
    const cliente = this.formFacturacion().controlValue().cliente;
    if (!cliente) return true;
    return !cliente.numero_celular;
  });
  showWhatsAppPhoneInput = computed(() => {
    return this.sendWhatsApp() && this.disableWhatsApp();
  });
  productos = signal<IProductoData[]>([]);
  terminoBusqueda = '';
  readonly subtotal = computed(() =>
    this.items().reduce((s, i) => s + i.cantidad * i.precio_unitario, 0),
  );
  readonly descuentoLineas = computed(() =>
    this.items().reduce((s, i) => s + (i.descuento ?? 0), 0),
  );
  readonly total = computed(() =>
    redondearDosDecimales(
      this.subtotal() - this.descuentoLineas() - this.formFacturacion().controlValue().descuento,
    ),
  );
  readonly opcionesIgv = environment.igvOptions.map((igv) => ({
    label: `${(igv * 100).toFixed(0)}%`,
    valor: igv,
  }));

  selectTipoDocumento(tipo: ETipoDocumentoSUNAT) {
    this.formFacturacion().controlValue().tipo_documento = tipo;
    this.localStorageService.setUltimoTipoDocumentoSUNAT(tipo);
  }
  cargarProductos() {
    this.productoApi
      .paginar(1, 200)
      .subscribe((r: any) => this.productos.set(r.data.filter((p: IProductoData) => p.activo)));
  }
  cargarCategorias() {
    this.categoriaApi.cargar().subscribe((r) => this.categorias.set(r));
  }
  abrirCrearProducto() {
    this.modalCrearProducto.set(true);
  }
  onProductoCreado(producto: IProductoData | undefined) {
    this.modalCrearProducto.set(false);
    if (producto) {
      this.cargarProductos();
      this.agregarProductoDb(producto);
    }
  }
  buscarProducto() {
    (this.terminoBusqueda.trim()
      ? this.productoApi.buscarProducto(this.terminoBusqueda)
      : this.productoApi.paginar(1, 200)
    ).subscribe((r: any) => this.productos.set(r.data.filter((p: IProductoData) => p.activo)));
  }
  agregarProductoDb(producto: IProductoData) {
    this.items.update((l) => [
      ...l,
      {
        producto_id: producto.id,
        descripcion: producto.nombre,
        cantidad: 1,
        precio_unitario: producto.precio_venta_final ?? producto.precio_venta,
        unidad_de_medida: producto.unidad_medida || 'NIU',
      },
    ]);
  }
  agregarProductoManual() {
    const m = this.productoManual();
    if (!m.descripcion || m.cantidad <= 0 || m.precio_unitario <= 0) return;
    this.items.update((l) => [
      ...l,
      {
        descripcion: m.descripcion,
        cantidad: m.cantidad,
        precio_unitario: m.precio_unitario,
        unidad_de_medida: m.unidad,
        descuento: m.descuento || undefined,
      },
    ]);
    this.productoManual.set({
      descripcion: '',
      cantidad: 1,
      precio_unitario: 0,
      unidad: 'NIU',
      descuento: 0,
    });
  }
  quitarItem(i: number) {
    this.items.update((l) => l.filter((_, idx) => idx !== i));
  }
  emitir() {
    if (!this.items().length) {
      this.toastService.show('top-right', 'error', 'Error', 'Agrega al menos un producto');
      return;
    }
    this.cargando.set(true);
    const fv = this.formFacturacion().controlValue();
    const c = fv.cliente;
    const payload: any = {
      tipo_documento: fv.tipo_documento,
      cliente_id: c?.id ?? 0,
      cliente_dniRuc: c?.dniRuc ?? '99999999',
      cliente_nombre_completo: c?.nombre_completo ?? 'CLIENTE VARIOS',
      cliente_direccion: c?.direccion || '-',
      cliente_tipo_documento: c?.tipo_documento || ETipoDocumento.DNI,
      items: this.items(),
      igv_porcentaje: fv.igv_porcentaje,
      descuento: fv.descuento || undefined,
      observaciones: fv.observaciones || undefined,
      descontar_inventario: fv.descontar_inventario || undefined,
    };
    if (this.sendWhatsApp()) {
      payload.send_whatsapp = true;
      if (this.disableWhatsApp()) {
        payload.whatsapp_number = `${this.whatsAppCountryCode()}${this.whatsAppNumber()}`;
      } else {
        payload.whatsapp_number = c?.numero_celular || undefined;
      }
    }
    this.facturacionApi.emitir(payload).subscribe({
      next: (res: any) => {
        this.cargando.set(false);
        if (res?.success !== false && res?.payload?.estado !== 'RECHAZADO') {
          this.localStorageService.setUltimoTipoDocumentoSUNAT(fv.tipo_documento);
          this.toastService.show(
            'top-right',
            'success',
            'Emitido',
            `${fv.tipo_documento} ${res?.serie || ''}-${res?.correlativo || ''} enviado a SUNAT`,
          );
          this.items.set([]);
          fv.observaciones = '';
          fv.descuento = 0;
          this.sendWhatsApp.set(false);
          this.whatsAppNumber.set('');
          this.whatsAppCountryCode.set('+51');
          this.cargarHistorial();
        } else {
          this.toastService.show(
            'top-right',
            'error',
            'Error SUNAT',
            res?.message || 'Error al emitir',
          );
        }
      },
      error: (err: any) => {
        this.cargando.set(false);
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'Error al emitir',
        );
      },
    });
  }

  // ═══════════ GUÍAS ═══════════
  formGuia = signal({
    tipo_guia: 'REMITENTE' as 'REMITENTE' | 'TRANSPORTISTA',
    serie: 'T001',
    motivo_traslado: '01',
    motivo_traslado_descripcion: '',
    modalidad_de_transporte: '02',
    peso_bruto_total: 0,
    peso_bruto_unidad_medida: 'KGM',
    numero_bultos: null as number | null,
    destinatario_tipo_documento: '6',
    destinatario_numero_documento: '',
    destinatario_denominacion: '',
    destinatario_direccion: '',
    punto_partida_ubigeo: '',
    punto_partida_direccion: '',
    punto_llegada_ubigeo: '',
    punto_llegada_direccion: '',
    fecha_inicio_traslado: new Date().toISOString().slice(0, 10),
  });
  transportista = signal<ITransportista | null>(null);
  itemsGuia = signal<IItemGuia[]>([]);
  docsRelacionados = signal<IDocumentoRelacionado[]>([]);
  nuevoItemGuia = signal<IItemGuia>({
    codigo_interno: '',
    descripcion: '',
    unidad_de_medida: 'NIU',
    cantidad: 1,
  });
  nuevoDoc = signal<IDocumentoRelacionado>({
    documento: 'factura',
    serie: '',
    numero: 0,
    ruc_emisor: '',
  });
  motivosTraslado = MOTIVOS_TRASLADO;
  modalidades = MODALIDADES;
  agregarItemGuia() {
    const i = this.nuevoItemGuia();
    if (!i.descripcion || i.cantidad <= 0) return;
    this.itemsGuia.update((l) => [...l, { ...i }]);
    this.nuevoItemGuia.set({
      codigo_interno: '',
      descripcion: '',
      unidad_de_medida: 'NIU',
      cantidad: 1,
    });
  }
  quitarItemGuia(i: number) {
    this.itemsGuia.update((l) => l.filter((_, idx) => idx !== i));
  }
  agregarDoc() {
    const d = this.nuevoDoc();
    if (!d.serie || !d.numero) return;
    this.docsRelacionados.update((l) => [...l, { ...d }]);
    this.nuevoDoc.set({ documento: 'factura', serie: '', numero: 0, ruc_emisor: '' });
  }
  quitarDoc(i: number) {
    this.docsRelacionados.update((l) => l.filter((_, idx) => idx !== i));
  }
  onClienteSeleccionado(cliente: IClienteData | null) {
    if (!cliente) return;
    const f = this.formGuia();
    f.destinatario_tipo_documento = cliente.tipo_documento === 'RUC' ? '6' : '1';
    f.destinatario_numero_documento = cliente.dniRuc;
    f.destinatario_denominacion = cliente.nombre_completo;
    f.destinatario_direccion = cliente.direccion || '';
  }
  crearGuia() {
    const f = this.formGuia();
    if (
      !f.destinatario_numero_documento ||
      !f.destinatario_denominacion ||
      !this.itemsGuia().length
    ) {
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'Completa destinatario y al menos un ítem',
      );
      return;
    }
    this.guiaApi
      .crear({
        ...f,
        numero_bultos: f.numero_bultos ?? undefined,
        items: this.itemsGuia(),
        documentos_relacionados: this.docsRelacionados().length
          ? this.docsRelacionados()
          : undefined,
        transportista: this.transportista() ?? undefined,
      } as ICrearGuiaRemisionReq)
      .subscribe({
        next: (res: any) => {
          this.toastService.show(
            'top-right',
            'success',
            'Creada',
            `Guía ${f.serie}-${res.correlativo} creada`,
          );
          this.cargarHistorial();
        },
        error: (err: any) =>
          this.toastService.show(
            'top-right',
            'error',
            'Error',
            err.error?.message || 'Error al crear',
          ),
      });
  }
  emitirGuia(id: number) {
    this.guiaApi.emitir(id).subscribe({
      next: () => {
        this.toastService.show('top-right', 'success', 'Enviada', 'Guía enviada a SUNAT');
        this.cargarHistorial();
      },
      error: (err: any) =>
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'Error al emitir',
        ),
    });
  }

  // ═══════════ HISTORIAL ═══════════
  itemsPorPagina = 15;
  paginacion = signal<IPaginar<any> | null>(null);
  opcionesTipo = OPCIONES_TIPO;
  opcionesEstado = OPCIONES_ESTADO;
  EAtajoRangoTiempo = EAtajoRangoTiempo;

  formHistorial = form(
    signal({
      rangoFecha: null as Date[] | null,
      tipo: 'TODOS' as ETipoHistorial,
      estado: null as string | null,
    }),
  );

  columnasHistorial: ITablaColumna[] = [
    { key: 'tipo', titulo: 'Tipo' },
    { key: 'numero', titulo: 'N°' },
    { key: 'destinatario', titulo: 'Cliente/Dest' },
    { key: 'total', titulo: 'Total' },
    { key: 'estado', titulo: 'Estado' },
    { key: 'created_at', titulo: 'Fecha' },
  ];

  atajoRangoFecha(tiempo: EAtajoRangoTiempo) {
    const { inicio, fin } = calcularAtajoRangoFecha(tiempo);
    this.formHistorial.rangoFecha().value.set([inicio, fin]);
  }
  limpiarFiltros() {
    this.formHistorial.rangoFecha().value.set(null);
    this.formHistorial.tipo().value.set('TODOS');
    this.formHistorial.estado().value.set(null);
  }

  cambiarPagina(pagina: number) {
    this.cargarHistorial(pagina);
  }

  cargarHistorial(pagina: number = 1) {
    const f = this.formHistorial().controlValue();
    const tipo = f.tipo;

    if (tipo === 'GRE') {
      this.guiaApi.listar(pagina, this.itemsPorPagina).subscribe((r: any) => {
        const items = (r?.data || []).map((e: any) => ({
          ...e,
          tipo: e.tipo_guia === 'REMITENTE' ? '🚚 GRE Remitente' : '🚛 GRE Transportista',
          numero: `${e.serie}-${e.correlativo}`,
          destinatario: e.destinatario_denominacion,
          total: 0,
          estado: this.estadoBadge(e.estado_sunat || e.estado),
          created_at: new Date(e.created_at).toLocaleString(),
        }));
        this.paginacion.set({ data: items, total: r.total, pagina: r.pagina, size: r.size });
      });
    } else if (tipo === 'TODOS') {
      this.facturacionApi.listar(pagina, this.itemsPorPagina).subscribe((r: any) => {
        const emisiones = (r?.data || []).map((e: any) => ({
          ...e,
          tipo: e.tipo_documento === 'FACTURA' ? '📄 Factura' : '📋 Boleta',
          numero: `${e.serie}-${e.correlativo}`,
          destinatario: e.cliente_nombre,
          estado: this.estadoBadge(e.estado_sunat),
          created_at: new Date(e.created_at).toLocaleString(),
        }));
        this.paginacion.set({ data: emisiones, total: r.total, pagina: r.pagina, size: r.size });
      });
    } else {
      this.facturacionApi.listar(pagina, this.itemsPorPagina).subscribe((r: any) => {
        const emisiones = (r?.data || [])
          .filter((e: any) => e.tipo_documento === tipo)
          .map((e: any) => ({
            ...e,
            tipo: e.tipo_documento === 'FACTURA' ? '📄 Factura' : '📋 Boleta',
            numero: `${e.serie}-${e.correlativo}`,
            destinatario: e.cliente_nombre,
            estado: this.estadoBadge(e.estado_sunat),
            created_at: new Date(e.created_at).toLocaleString(),
          }));
        this.paginacion.set({
          data: emisiones,
          total: emisiones.length,
          pagina: pagina,
          size: this.itemsPorPagina,
        });
      });
    }
  }

  aplicarFiltros(event: Event) {
    event.preventDefault();
    this.cargarHistorial(1);
  }

  private estadoBadge(estado: string): string {
    const m: Record<string, string> = {
      ACEPTADO: '✅ Aceptado',
      RECHAZADO: '❌ Rechazado',
      PENDIENTE: '⏳ Pendiente',
      BORRADOR: '📝 Borrador',
      ANULADO: '🚫 Anulado',
      ENVIADO: '📤 Enviado',
    };
    return m[estado] || estado;
  }

  // ─── Modales ───
  modalAnular = signal(false);
  emisionAAnular = signal<any>(null);
  motivoAnulacion = '';
  modalDetalle = signal(false);
  emisionDetalle = signal<any>(null);
  abrirDetalle(e: any) {
    this.emisionDetalle.set(e);
    this.modalDetalle.set(true);
  }
  abrirAnular(e: any) {
    this.emisionAAnular.set(e);
    this.motivoAnulacion = '';
    this.modalAnular.set(true);
  }
  confirmarAnular() {
    if (!this.motivoAnulacion.trim()) return;
    this.facturacionApi.anular(this.emisionAAnular()?.id, this.motivoAnulacion).subscribe({
      next: () => {
        this.toastService.show('top-right', 'success', 'Anulado', 'Comprobante anulado');
        this.modalAnular.set(false);
        this.cargarHistorial();
      },
      error: (err: any) =>
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'Error al anular',
        ),
    });
  }
  reemitir(id: number) {
    this.facturacionApi.reemitir(id).subscribe({
      next: () => {
        this.toastService.show('top-right', 'success', 'Reemitido', 'Reenviado a SUNAT');
        this.cargarHistorial();
      },
      error: (err: any) =>
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'Error al reemitir',
        ),
    });
  }

  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.cargarProductos();
        this.cargarCategorias();
        this.cargarHistorial();
      }
    });
  }
  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Facturación',
      subtitle: 'Comprobantes electrónicos y Guías de Remisión',
      showBackButton: true,
      icon: 'bi bi-receipt',
    });
  }
}
